/* istanbul ignore file */
import React, { useState, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";

import { TableCell, SvgIcon, Typography, Box } from "@material-ui/core/";
import CircularProgress from "@mui/material/CircularProgress";

import filesize from "filesize";

import SmallOutlinedButton from "../../Button/SmallOutlinedButton/SmallOutlinedButton";

import { ReactComponent as blueDownArrow } from "../../../assets/images/arrowdown.svg";
import { ReactComponent as blueUpArrow } from "../../../assets/images/arrowup.svg";
import { ReactComponent as grayDownArrow } from "../../../assets/images/arrowgray.svg";
import addToCartImage from "../../../assets/images/32x32_AddShoppingCartRounded.svg";
import removeFromCartImage from "../../../assets/images/32x32_RemoveShoppingCartRounded.svg";

import classes from "../GenomeTableCells/GenomeTableCells.module.css";

import { ROW_SELECTED_NONE } from "../Constants";

import TransientTooltip from "../../UI/Tooltip/TransientTooltip/TransientTooltip";
import ShoppingCart from "../../UI/ShoppingCart/ShoppingCart";

import { getProposalFiles } from "../../../utils/HttpClientProvider";

import FileAndPageContext, {
  useFileAndPageContext,
} from "../../../context/FileAndPageContext";

const ProposalTableCells = (props) => {
  const {
    row,
    onRowClick,
    expanded,
    isHovering,
    honeycomb,
    addFilesToCart,
    setProposalMetaList,
  } = props;

  const [cartUpdated, setCartUpdated] = useState(false);

  const [files, setFiles] = useState(row.total_file_count > 0 ? null : []);
  const { deleteProposalFilesFromCart } = useContext(FileAndPageContext);

  const { cartData } = useFileAndPageContext();

  const history = useHistory();

  const allInCart =
    cartData && files?.length > 0
      ? cartData[row.id] === files[0].proposal_unique_file_count
      : false;

  useEffect(() => {
    let cancelled = false;

    // page size of row.total_file_count should be OK even if some files in proposal datasets without metadata.proposal_id.
    // in the extreme case of all files in separate datasets, all files should have metadata.proposal_id
    if (row.total_file_count > 0) {
      const promise = getProposalFiles(row.id, row.total_file_count);
      promise.then((data) => {
        if (!cancelled && !data.error) {
          setFiles(data.files);
        } else {
          setFiles([]);
        }
      });
    }

    return () => {
      cancelled = true;
    };
  }, []);

  const doCartUpdated = () => {
    setCartUpdated(true);
    setTimeout(() => {
      setCartUpdated(false);
    }, 1500);
  };

  const addToCart = (event) => {
    event.stopPropagation();

    if (allInCart) {
      honeycomb.sendUiInteractionSpan("rm-proposal-files-to-cart", {
        data: { proposal_id: row.id, file_count: files?.length },
      });
      deleteProposalFilesFromCart(row.id, files);
      doCartUpdated();
    } else {
      honeycomb.sendUiInteractionSpan("add-proposal-files-to-cart", {
        data: { proposal_id: row.id, file_count: files?.length },
      });
      addFilesToCart(files);
    }
  };

  const upDownArrow = (hovering, isExpanded) => {
    if (!hovering && !isExpanded) {
      return grayDownArrow;
    }
    if (hovering && !isExpanded) {
      return blueDownArrow;
    }
    if (isExpanded) {
      return blueUpArrow;
    }
  };

  const arrowLabel = (hovering, isExpanded, currentRow) => {
    if (!hovering && !isExpanded) {
      return `Expandable ${currentRow.name} Proposal`;
    }
    if (hovering && !isExpanded) {
      return `Expand ${currentRow.name} Proposal`;
    }
    if (isExpanded) {
      return `Collapse ${currentRow.name} Proposal`;
    }
  };

  const click = {
    className: classes.clickable,
    onClick: (event) => {
      event.stopPropagation();
      onRowClick(event, row.id);
    },
  };

  const thPros = {
    component: "th",
    scope: "row",
  };

  const inCartFileCount = cartData ? cartData[row.id] : 0;
  const cartAddOrRemoveIcon = (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <img
      className={
        allInCart ? classes.addToCartIcon32 : classes.removeFromCartIcon32
      }
      src={allInCart ? removeFromCartImage : addToCartImage}
      alt="cart"
      onClick={addToCart}
    />
  );

  const cartAddOrRemoveIconWithTip = cartAddOrRemoveIcon ? (
    <TransientTooltip
      noticeLabel={
        cartUpdated
          ? !allInCart
            ? "Proposal datasets removed from cart"
            : "Proposal datasets added to cart"
          : null
      }
      labelConf={
        cartUpdated
          ? null
          : [
              {
                type: "title",
                value: allInCart
                  ? `Remove all ${files?.length} proposal files from the cart`
                  : `Add ${files?.length > 1 ? "all" : "the"} ${
                      files?.length
                    } proposal file${files?.length > 1 && "s"} to the cart`,
              },
            ]
      }
      child={cartAddOrRemoveIcon}
      open={cartUpdated}
      bottomOffset={0}
    />
  ) : null;

  const inCartCountMsg =
    inCartFileCount > 0 ? ` / ${inCartFileCount} in cart` : "";

  return (
    <>
      <TableCell
        {...thPros}
        style={{ height: 42, width: 40, align: "center" }}
        {...click}
        component="td"
      >
        <SvgIcon
          viewBox="0 0 48 48"
          component={upDownArrow(isHovering, expanded)}
          alt={arrowLabel(isHovering, expanded, row)}
          aria-label={arrowLabel(isHovering, expanded, row)}
          data-testid={`${
            expanded ? "expanded" : "collapsed"
          }-proposal-row-icon`}
          style={{
            verticalAlign: "middle",
            width: 48,
            height: 48,
          }}
        />
      </TableCell>
      <TableCell {...thPros} {...click} style={{ padding: 0 }} component="td">
        <table>
          <tbody>
            <tr>
              {inCartFileCount > 0 && (
                <td style={{ paddingRight: 2, paddingLeft: 2, paddingTop: 10 }}>
                  <ShoppingCart
                    count={inCartFileCount}
                    tip="File(s) in the Cart"
                    badgeBackgroundColor={classes.lake700}
                  />
                </td>
              )}

              <td>
                <Typography variant="subtitle1">Proposal {row.id}</Typography>

                <Typography
                  variant="h3"
                  className={classes.h3Color}
                  data-testid="genome-row-name"
                >
                  {row.title}
                </Typography>
              </td>
            </tr>
          </tbody>
        </table>
      </TableCell>
      <TableCell {...thPros} {...click} component="td" style={{ width: 400 }}>
        <Typography variant="subtitle1">Number of files</Typography>
        <Typography variant="h3" className={classes.h3Color}>
          {files ? (
            `${
              files.length > 0 ? files[0].proposal_unique_file_count : 0
            }${inCartCountMsg}`
          ) : (
            <CircularProgress />
          )}
        </Typography>
      </TableCell>
      <TableCell {...thPros} {...click} component="td" style={{ width: 130 }}>
        <Typography variant="subtitle1">Total file size</Typography>
        <Typography variant="h3" className={classes.h3Color}>
          {row.total_file_size > 0
            ? filesize(row.total_file_size, { round: 1 })
            : row.total_file_size}
        </Typography>
      </TableCell>

      <TableCell align="right" {...click} style={{ width: 100 }}>
        {files?.length > 0 && (
          <Box className="genomeRowButtons">{cartAddOrRemoveIconWithTip}</Box>
        )}
      </TableCell>
      <TableCell
        {...thPros}
        {...click}
        component="td"
        style={{ borderRight: `solid 1px #949494`, width: 40 }} // safari: row border get lost
      >
        {files?.length > 0 && (
          <SmallOutlinedButton
            label="View"
            callback={(event) => {
              event.stopPropagation();
              setProposalMetaList();
              history.push(
                `/search?q=JGI_ID%3A%60${row.id}%60&t=advanced&showview=single`
              );
            }}
            sx={{ width: 82, height: 32 }}
          />
        )}
      </TableCell>
    </>
  );
};

export default ProposalTableCells;

ProposalTableCells.defaultProps = {
  ldClient: null,
  row: {},
  onRowClick: () => {},
  expanded: false,
  isHovering: false,
  honeycomb: {
    sendUiInteractionSpan: () => {},
  },
  selectState: ROW_SELECTED_NONE,
  addFilesToCart: null,
  selectedFiles: null,
};

ProposalTableCells.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    total_file_count: PropTypes.number,
    files: PropTypes.arrayOf(PropTypes.shape()),
    total_file_size: PropTypes.number,
    title: PropTypes.string,
    lay_description: PropTypes.string,
    date_approved: PropTypes.string,
    pi: PropTypes.shape(),
    award_doi: PropTypes.string,
    year: PropTypes.number,
  }),
  honeycomb: PropTypes.shape({
    sendUiInteractionSpan: PropTypes.func,
  }),
  ldClient: PropTypes.objectOf(Object),
  onRowClick: PropTypes.func,
  expanded: PropTypes.bool,
  isHovering: PropTypes.bool,
  selectState: PropTypes.number,
  addFilesToCart: PropTypes.func,
  selectedFiles: PropTypes.arrayOf(PropTypes.shape()),
  setProposalMetaList: PropTypes.func.isRequired,
};
